import moment from 'moment';
import request from './request';
import { effectiveOrderWeight, summarizeCapturedItems } from './orderWeight';
import { downloadCsv, escapeCsvField } from './csv';
import { addressZipLocality } from './stopFilter';

// Re-exported for the exports that already import it from here.
export { escapeCsvField };

// Reverse of MAP_CSV_DATA in UploadOrders.jsx — maps API field → CSV header.
//
// `carrierTrackingRef` is a server-computed field (not stored on Order). The
// export flow fetches it via `POST /orders/carrier-tracking-refs` and merges
// it into the row before writing the CSV, so partners see the same tracking
// reference here as in their Bean webhook payload.
export const ORDER_CSV_FIELD_MAP = [
  ['orderRef', 'Order Ref'],
  ['deliveryDate', 'Delivery Date'],
  ['load', 'Load'],
  ['name', 'Name'],
  ['description', 'Description'],
  ['sizeLength', 'Length(cm)'],
  ['sizeWidth', 'Width(cm)'],
  ['sizeHeight', 'Height(cm)'],
  ['weight', 'Weight(kg)'],
  ['pickupName', 'Pickup Name'],
  ['pickupAddress', 'Pickup Address'],
  ['pickupAddressLat', 'Pickup Address Latitude'],
  ['pickupAddressLng', 'Pickup Address Longitude'],
  ['pickupTimeStart', 'Pickup Time Start'],
  ['pickupTimeEnd', 'Pickup Time End'],
  ['pickupPhone', 'Pickup Phone'],
  ['pickupNote', 'Pickup Note'],
  ['pickupDuration', 'Pickup Duration'],
  ['deliveryName', 'Delivery Name'],
  ['deliveryEmail', 'Delivery Email'],
  ['deliveryAddress', 'Delivery Address'],
  ['deliveryAddressLat', 'Delivery Address Latitude'],
  ['deliveryAddressLng', 'Delivery Address Longitude'],
  ['deliveryPhone', 'Delivery Phone'],
  ['deliveryTimeStart', 'Delivery Time Start'],
  ['deliveryTimeEnd', 'Delivery Time End'],
  ['deliveryNote', 'Delivery Note'],
  ['deliveryDuration', 'Delivery Duration'],
  ['partner', 'Partner'],
  ['partnerContact', 'Partner Contact'],
  ['partnerOrderRef', 'Partner Order Ref'],
  ['tourRef', 'Tour Ref'],
  ['carrierTrackingRef', 'Carrier Tracking Ref'],
  ['airtableOrderRecordRef', 'Airtable Order Record Ref'],
  ['preparationMode', 'Preparation Mode'],
  ['collectionForm', 'Collection Form'],
  ['types', 'Types'],
  ['volume', 'Volume'],
  ['status', 'Status'],
  // OVO-027 — driver/operator-captured product info. Appended at the
  // end so existing column positions stay stable for partners parsing
  // the CSV by index. Each cell is a CollectedItem-derived value
  // (semicolon-joined when an order has multiple captured products).
  ['capturedProductTypes', 'Captured Product Types'],
  ['capturedWeight', 'Captured Weight'],
  ['capturedUnit', 'Captured Unit'],
  ['capturedBy', 'Captured By'],
  ['capturedAt', 'Captured At'],
  // #91 — SIG crate collections: the codes scanned at pickup (one per crate,
  // semicolon-joined) + the route, so ops can reconcile with the depot's
  // own stock-entry scan. Appended for the same column-stability reason.
  ['routeId', 'Route ID'],
  ['capturedCodes', 'Captured Codes'],
  ['capturedCount', 'Captured Count'],
  // #47 — CT bills communes per collected kilo: the postal code + locality in
  // their own columns (they're inside the address otherwise), and the box the
  // order serviced. Appended for the same column-stability reason.
  ['pickupZip', 'Pickup NPA'],
  ['pickupLocality', 'Pickup Locality'],
  ['deliveryZip', 'Delivery NPA'],
  ['deliveryLocality', 'Delivery Locality'],
  ['collectionBox', 'Collection Box'],
];

/**
 * Fetch `{ orderCode: carrierTrackingRef }` for the given orders. Best-effort
 * — if the endpoint fails (network, 403, etc.) returns an empty map so the
 * CSV export still goes through without the Carrier Tracking Ref column
 * filled in.
 */
async function fetchCarrierTrackingRefs(orderCodes) {
  if (!orderCodes || orderCodes.length === 0) return {};
  try {
    const { response } = await request('/orders/carrier-tracking-refs', {
      method: 'POST',
      data: { orderCodes },
    });
    return response || {};
  } catch {
    return {};
  }
}

// Scanned codes of the order's PRODUCT captures (the admin listing selects
// them; the partner-viewer listing doesn't, so viewers get empty cells).
function capturedCodes(order) {
  return (order?.collectedItems || []).map((ci) => ci.scannedCode).filter(Boolean);
}

export async function downloadOrdersCsv(orders, filenamePrefix = 'orders-export') {
  const carrierRefs = await fetchCarrierTrackingRefs(orders.map((o) => o.orderCode).filter(Boolean));
  const headers = ORDER_CSV_FIELD_MAP.map(([, header]) => header);
  const rows = orders.map((order) => {
    // OVO-027 — captured-item summary is per-order so we compute it once
    // and reuse across all five Captured* columns instead of re-summing
    // per cell.
    const captured = summarizeCapturedItems(order);
    const pickupZip = addressZipLocality(order.pickupAddress);
    const deliveryZip = addressZipLocality(order.deliveryAddress);
    return ORDER_CSV_FIELD_MAP.map(([field]) => {
      let value = order[field];
      if (field === 'deliveryDate' && value) {
        value = moment(value).format('DD-MM-YYYY');
      }
      if (field === 'types' && Array.isArray(value)) {
        value = value.join(',');
      }
      if (field === 'partner' && !value) {
        value = order?.partnerRef?.name || '';
      }
      if (field === 'carrierTrackingRef') {
        value = carrierRefs[order.orderCode] || '';
      }
      // OVO-027 — when the dispatcher left Order.weight blank but the driver
      // captured a weight at pickup (via CollectedItem.weight), fall back to
      // the captured value. Previously the CSV showed an empty cell for
      // every driver-weighted order, which broke billing reconciliation.
      if (field === 'weight') {
        value = effectiveOrderWeight(order);
      }
      // OVO-027 — five captured-item columns flatten the CollectedItem
      // payload into CSV-friendly scalars. Empty cells when the order
      // has no PRODUCT-category capture.
      if (field === 'capturedProductTypes') value = captured?.productTypes ?? '';
      if (field === 'capturedWeight') value = captured?.weight ?? '';
      if (field === 'capturedUnit') value = captured?.unit ?? '';
      if (field === 'capturedBy') value = captured?.capturedBy ?? '';
      if (field === 'capturedAt') value = captured?.capturedAt ?? '';
      if (field === 'capturedCodes') value = capturedCodes(order).join('; ');
      if (field === 'capturedCount') value = capturedCodes(order).length || '';
      if (field === 'pickupZip') value = pickupZip?.zip ?? '';
      if (field === 'pickupLocality') value = pickupZip?.locality ?? '';
      if (field === 'deliveryZip') value = deliveryZip?.zip ?? '';
      if (field === 'deliveryLocality') value = deliveryZip?.locality ?? '';
      if (field === 'collectionBox') {
        const cp = order.collectionPoint;
        value = cp ? cp.externalRef || cp.name || '' : '';
      }
      return escapeCsvField(value);
    });
  });
  downloadCsv(
    [headers.join(','), ...rows.map((r) => r.join(','))],
    `${filenamePrefix}-${moment().format('YYYY-MM-DD')}.csv`,
  );
}
